import { anticipoAplicado, anticipoDisponible, getNombreCuenta, getTipoCuentaPUC } from "../core/accounting.js";
import { BRAND, EMPRESA } from "../core/config.js";
import { state } from "../core/state.js";
import { parseNumber, today } from "../core/utils.js";
import { liquidarComisiones } from "../modules/comisiones.js";
import { calcularEstadoResultados, getSaldoCuentaRango } from "../modules/contabilidad.js";
import { getLiquidacionOP, getMontoPagadoOCP, getSaldoOCP } from "../modules/documentos.js";
import { getSaldoFactura } from "../modules/facturas.js";

export function exportarExcelAnticipos() {
  const encabezados = [
    "OP","Fecha OP","Cliente","NIT","Total OP COP",
    "Fecha Anticipo","Monto Anticipo COP","% sobre OP",
    "Medio de pago","Referencia","Nota",
    "Estado","Aplicado COP","Por cruzar COP",
    "Factura cruzada","Fecha cruce","Asiento entrada","Asiento cruce"
  ];
  const filas = [];
  (state.data.pedidos||[]).forEach(op => {
    if (!op._anticipos || op._anticipos.length===0) return;
    const cl = (state.data.clientes||[]).find(c=>c.id===op.cliente_id)||{};
    const totalOP = parseNumber(op.total)||0;
    op._anticipos.forEach(ant => {
      const monto  = parseNumber(ant.monto)||0;
      const pct    = totalOP>0 ? +(monto/totalOP*100).toFixed(2) : 0;
      const fc     = ant._cruzadoFactura ? (state.data.facturas||[]).find(f=>f.id===ant._cruzadoFactura) : null;
      const astEnt = ant._asientoId   ? (state.data.asientos||[]).find(a=>a.id===ant._asientoId)   : null;
      const astCrz = ant._asientoCruceId ? (state.data.asientos||[]).find(a=>a.id===ant._asientoCruceId) : null;
      filas.push([
        op.numero||"", op.fecha||"", cl.nombre||"", cl.nit||"", totalOP,
        ant.fecha||"", monto, pct,
        ant.medio||"", ant.referencia||"", ant.nota||"",
        anticipoDisponible(ant) <= 0.01 ? "Cruzado"
          : anticipoAplicado(ant) > 0 ? "Parcial" : "Pendiente",
        anticipoAplicado(ant), anticipoDisponible(ant),
        fc?fc.numero:"", fc?fc.fecha:"",
        astEnt?astEnt.numero:"", astCrz?astCrz.numero:""
      ]);
    });
  });
  // El total suma el recibido, lo aplicado y lo que queda vivo: el saldo del
  // pasivo 2710 es la última columna, no la del monto recibido.
  const totCol = (i) => filas.reduce((s,r)=>s+(Number(r[i])||0),0);
  filas.push(["TOTAL","","","","","",totCol(6),"","","","",
              "",totCol(12),totCol(13),"","","",""]);
  csvDescargar(`${BRAND.archivo_prefijo}_Anticipos_${today()}`, encabezados, filas);
}

export function csvDescargar(nombre, encabezados, filas) {
  const esc = v => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[";,\n\r]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s;
  };
  const lines = [encabezados.map(esc).join(";")]
    .concat(filas.map(row => row.map(esc).join(";")));
  const csv = "\uFEFF" + lines.join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre + ".csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportarExcelCT() {
  const clientes = state.data.clientes || [];
  const encabezados = [
    "Número","Fecha","Cliente","NIT/CC","Asesor Comercial","Estado",
    "Vigencia (días)","Subtotal COP","IVA COP","Total COP","Ítems"
  ];
  const filas = (state.data.cotizaciones || []).map(ct => {
    const cliente = clientes.find(c => c.id === ct.cliente_id) || {};
    return [
      ct.numero || "", ct.fecha || "",
      cliente.nombre || "", cliente.nit || "",
      ct.asesor_comercial || "", ct.estado || "",
      ct.dias_vigencia || "",
      parseNumber(ct.subtotal) || 0,
      parseNumber(ct.iva) || 0,
      parseNumber(ct.total) || 0,
      (ct.items || []).map(it => `${it.articulo} x${it.cantidad}`).join(" | ")
    ];
  });
  csvDescargar(`${BRAND.archivo_prefijo}_CT_${today()}`, encabezados, filas);
}

export function exportarExcelCartera() {
  const clientes = state.data.clientes || [];
  const hoy = new Date(today() + "T00:00:00");
  const encabezados = [
    "Factura","Fecha Emisión","Fecha Vencimiento","Cliente","NIT/CC",
    "Total Factura COP","Abonado COP","Saldo COP",
    "Días Vencido","Bucket","Estado"
  ];
  const filas = [];
  (state.data.facturas || [])
    .filter(f => (f.estado === "emitida" || f.estado === "vencida") && getSaldoFactura(f) > 0.01)
    .sort((a, b) => {
      const da = a.fecha_vencimiento || a.fecha || "";
      const db = b.fecha_vencimiento || b.fecha || "";
      return da.localeCompare(db);
    })
    .forEach(f => {
      const cliente = clientes.find(c => c.id === f.cliente_id) || {};
      const saldo = getSaldoFactura(f);
      const venc = f.fecha_vencimiento ? new Date(f.fecha_vencimiento + "T00:00:00") : hoy;
      const dias = Math.floor((hoy - venc) / (1000 * 60 * 60 * 24));
      const bucket = dias <= 0 ? "Al día"
        : dias <= 30 ? "1-30 días"
        : dias <= 60 ? "31-60 días"
        : dias <= 90 ? "61-90 días"
        : "Más de 90 días";
      const abonado = (parseNumber(f.total) || 0) - saldo;
      filas.push([
        f.numero || "", f.fecha || "", f.fecha_vencimiento || "",
        cliente.nombre || "", cliente.nit || "",
        parseNumber(f.total) || 0, abonado, saldo,
        dias, bucket, f.estado || ""
      ]);
    });
  csvDescargar(`${BRAND.archivo_prefijo}_Cartera_${today()}`, encabezados, filas);
}

export function exportarExcelLibroDiario() {
  const anioActual = new Date().getFullYear();
  const desde = state.contabFiltros?.desde || `${anioActual}-01-01`;
  const hasta = state.contabFiltros?.hasta || `${anioActual}-12-31`;
  const encabezados = [
    "Número Asiento","Fecha","Concepto","Origen Tipo","Origen Número",
    "Cuenta","Nombre Cuenta","Débito COP","Crédito COP","Balanceado"
  ];
  const filas = [];
  (state.data.asientos || [])
    .filter(a => (!desde || a.fecha >= desde) && (!hasta || a.fecha <= hasta))
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .forEach(a => {
      (a.movimientos || []).forEach(m => {
        filas.push([
          a.numero || "", a.fecha || "", a.concepto || "",
          (a.origen || {}).tipo || "", (a.origen || {}).numero || "",
          m.cuenta || "", m.nombre || getNombreCuenta(m.cuenta),
          m.debito || 0, m.credito || 0,
          a.balanceado ? "Sí" : "No"
        ]);
      });
    });
  csvDescargar(`${BRAND.archivo_prefijo}_LibroDiario_${desde}_${hasta}`, encabezados, filas);
}

export function exportarExcelBalancePrueba() {
  const anioActual = new Date().getFullYear();
  const desde = state.contabFiltros?.desde || `${anioActual}-01-01`;
  const hasta = state.contabFiltros?.hasta || `${anioActual}-12-31`;
  const encabezados = ["Código","Cuenta","Naturaleza","Clase","Débitos COP","Créditos COP","Saldo COP"];
  const filas = (state.data.puc || [])
    .map(c => {
      const r = getSaldoCuentaRango(c.codigo, desde, hasta);
      return { codigo: c.codigo, nombre: c.nombre, naturaleza: c.naturaleza, ...r };
    })
    .filter(r => r.debito > 0 || r.credito > 0)
    .sort((a, b) => a.codigo.localeCompare(b.codigo))
    .map(r => [
      r.codigo, r.nombre,
      r.naturaleza === "D" ? "Débito" : "Crédito",
      getTipoCuentaPUC(r.codigo),
      r.debito, r.credito, r.saldo
    ]);
  const totD = filas.reduce((s, r) => s + (Number(r[4])||0), 0);
  const totC = filas.reduce((s, r) => s + (Number(r[5])||0), 0);
  filas.push(["","TOTAL","","", totD, totC, totD - totC]);
  csvDescargar(`${BRAND.archivo_prefijo}_Balance_${desde}_${hasta}`, encabezados, filas);
}

export function exportarExcelEstadoResultados() {
  const anioActual = new Date().getFullYear();
  const desde = state.contabFiltros?.desde || `${anioActual}-01-01`;
  const hasta = state.contabFiltros?.hasta || `${anioActual}-12-31`;
  const er = calcularEstadoResultados(desde, hasta);
  const encabezados = ["Concepto","Valor COP"];
  const filas = [
    ["ESTADO DE RESULTADOS — " + (EMPRESA.nombre || BRAND.producto), ""],
    ["Período: " + desde + " al " + hasta, ""],
    ["", ""],
    ["(+) Ingresos operacionales brutos (grupo 41)", er.ingresosOperacionales],
    ["(-) Devoluciones y descuentos en ventas (4175/4180)", -er.devoluciones],
    ["= INGRESOS NETOS", er.ingresosNetos],
    ["", ""],
    ["(-) Costo de ventas (grupo 61)", -er.costoVentas],
    ["= UTILIDAD BRUTA", er.utilidadBruta],
    ["", ""],
    ["(-) Gastos operacionales (grupos 51 y 52)", -er.gastosOperacionales],
    ["= UTILIDAD OPERACIONAL", er.utilidadOperacional],
    ["", ""],
    ["(+) Ingresos no operacionales (grupo 42)", er.ingresosFinancieros],
    ["(-) Gastos financieros (grupo 53)", -er.gastosFinancieros],
    ["= UTILIDAD ANTES DE IMPUESTO", er.utilidadAntesImpuesto],
    ["", ""],
    ["(-) Provisión impuesto de renta (" + (state.tasaRenta || 35) + "%)", -er.provisionRenta],
    ["= UTILIDAD NETA DEL PERÍODO", er.utilidadNeta],
    ["", ""],
    ["Generado por " + BRAND.producto + " — " + today(), ""]
  ];
  csvDescargar(`${BRAND.archivo_prefijo}_EstadoResultados_${desde}_${hasta}`, encabezados, filas);
}

export function exportarExcelComisiones() {
  const f = state.comisionesFiltro || {};
  const datos = liquidarComisiones(f.desde, f.hasta, f.vendedor);
  const clientes = state.data.clientes || [];
  const encabezados = [
    "OP","Fecha","Cliente","Asesor Comercial","Rango",
    "Venta Neta COP","Margen %","% Comisión","Comisión COP",
    "Margen Post-Comisión %","Ya Aplicada"
  ];
  const filas = datos.map(r => {
    const op = r.liqData && r.liqData.op ? r.liqData.op : {};
    const cliente = clientes.find(c => c.id === op.cliente_id) || {};
    return [
      r.opNumero || "", r.fecha || "",
      cliente.nombre || "",
      r.asesorNombre || "", r.rangoVendedor || "",
      r.ventaNeta,
      +(r.margen.toFixed(2)),
      +(r.comisionPct.toFixed(2)),
      r.comisionMonto,
      +(r.margenPost.toFixed(2)),
      r.yaAplicada ? "Sí" : "No"
    ];
  });
  const totVenta    = datos.reduce((s, r) => s + r.ventaNeta, 0);
  const totComision = datos.reduce((s, r) => s + r.comisionMonto, 0);
  filas.push(["TOTAL","","","","", totVenta,"","", totComision,"",""]);
  csvDescargar(`${BRAND.archivo_prefijo}_Comisiones_${today()}`, encabezados, filas);
}

export function exportarExcelOPs() { exportarExcelReportesOP(); }

export function exportarExcelReportesOP() {
  const clientes = state.data.clientes || [];
  const encabezados = [
    "OP","Fecha","Cliente","Asesor Comercial","Estado",
    "Venta Total COP","Costo OCP COP","Otros Costos COP","Costo Total COP",
    "Utilidad COP","Margen %","Margen Inicial %"
  ];
  let ops = state.data.pedidos || [];
  const rf = state.reporteFiltros || {};
  if (rf.cliente) ops = ops.filter(op => op.cliente_id === rf.cliente);
  if (rf.estado)  ops = ops.filter(op => op.estado === rf.estado);
  if (rf.fecha)   ops = ops.filter(op => op.fecha === rf.fecha);

  const filas = ops.map(op => {
    const liq = getLiquidacionOP(op.id);
    const cliente = clientes.find(c => c.id === op.cliente_id) || {};
    if (!liq) return [
      op.numero || "", op.fecha || "", cliente.nombre || "",
      op.asesor_comercial || "", op.estado || "",
      parseNumber(op.total) || 0, 0, 0, 0, 0, 0, ""
    ];
    return [
      op.numero || "", op.fecha || "", cliente.nombre || "",
      op.asesor_comercial || "", op.estado || "",
      liq.ventaTotal, liq.costoOcpTotal, liq.otrosCostosTotal, liq.costoTotal,
      liq.utilidad,
      +(liq.margen.toFixed(2)),
      liq.margenInicial != null ? +(liq.margenInicial.toFixed(2)) : ""
    ];
  });
  const totVenta = filas.reduce((s, r) => s + (Number(r[5])||0), 0);
  const totCosto = filas.reduce((s, r) => s + (Number(r[8])||0), 0);
  const totUtil  = filas.reduce((s, r) => s + (Number(r[9])||0), 0);
  filas.push(["TOTAL","","","","", totVenta,"","", totCosto, totUtil,"",""]);
  csvDescargar(`${BRAND.archivo_prefijo}_OPs_${today()}`, encabezados, filas);
}

export function exportarExcelCxP() {
  const proveedores = state.data.proveedores || [];
  const encabezados = [
    "OCP","Fecha OCP","Proveedor","NIT","Nº Factura Proveedor","Fecha Factura",
    "OP Origen","Estado Pago","Total COP","Saldo COP","Pagado COP"
  ];
  const ocps = (state.data.ocp||[]).filter(o => o._procesado && o._procesado.factura);
  const filas = ocps.map(o => {
    const prov = proveedores.find(p=>p.id===o.proveedor_id)||{};
    const op = o._origen ? (state.data.pedidos||[]).find(p=>p.id===o._origen.id) : null;
    const saldo = getSaldoOCP(o);
    const pagado = getMontoPagadoOCP(o);
    return [
      o.numero||"", o.fecha||"",
      prov.nombre||"", prov.nit||"",
      (o._procesado&&o._procesado.factura_numero)||"",
      (o._procesado&&o._procesado.factura_fecha_doc)||"",
      op?op.numero:"",
      o.estado_pago||"por_pagar",
      parseNumber(o.total)||0, saldo, pagado
    ];
  });
  const totTotal = filas.reduce((s,r)=>s+(Number(r[8])||0),0);
  const totSaldo = filas.reduce((s,r)=>s+(Number(r[9])||0),0);
  const totPag   = filas.reduce((s,r)=>s+(Number(r[10])||0),0);
  filas.push(["TOTAL","","","","","","","",totTotal,totSaldo,totPag]);
  csvDescargar(`${BRAND.archivo_prefijo}_CxP_${today()}`, encabezados, filas);
}
